/* eslint-disable */
import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { Circle } from 'src/app/components/map/google-map/circle/circle';
import { CircleConfig } from 'src/app/components/map/google-map/circle/circle.config';
import { GoogleMapsController } from 'src/app/components/map/google-map/controllers/google-maps.controller';
import { MarkerConfig } from 'src/app/components/map/google-map/marker/marker.config';
import { Polygon } from 'src/app/components/map/google-map/polygon/polygon';
import { PolygonConfig } from 'src/app/components/map/google-map/polygon/polygon.config';
import { Polyline } from 'src/app/components/map/google-map/polyline/polyline';
import { PolylineConfig } from 'src/app/components/map/google-map/polyline/polyline.config';
import { MSCameraConfig, MSLatLng, MSMapType } from 'src/app/models/map.model';
import { offsetLatLngAlongPerpendicular } from 'src/app/utils/google-map/offset-latlng.utils';
import { AssetWrapper } from 'src/app/wrappers/asset.wrapper';
import { CONSTANTS } from '../config.service';
import { GeocoderService } from '../geocode.service';
import { GisService } from '../gis.service';
import { GeolocationPermission } from '../location/geolocation-permission';
import { GeolocationStatus } from '../location/geolocation-status';
import { GeolocationService } from '../location/geolocation.service';
import { LoggerService } from '../logger.service';
import { PubSubService } from '../pub-sub.service';
import { MapsNativeMarker } from './maps-native-marker';
import { MSRemoteLoggerCategory } from 'src/app/adapters/remote-logger/utils/remote-logger.utils';

const POLYLINE_COLOR = '#4DB8DB';
const POLYLINE_MERGE_COLOR = 'purple';
const POLYELINE_WIDTH = 10;
const LOOP_OUTLINE_OFFSET = 0.0001;

@Injectable({
  providedIn: 'root'
})
export class MapsNativeService {
  private controller: GoogleMapsController;
  private subscriptions: Subscription[] = [];

  markers: MapsNativeMarker[] = [];
  points: MSLatLng[];
  circles: Circle[] = [];
  infoWindows: any = [];

  private paths: (Polyline | Polygon)[] = [];

  private currentZoom = 0;
  private cameraCenter: MSLatLng;

  constructor(
    public platform: Platform,
    private logger: LoggerService,
    private assetWrapper: AssetWrapper,
    private pubSub: PubSubService,
    private geolocationService: GeolocationService,
    private geocoderService: GeocoderService,
    private gis: GisService
  ) {
    this.logger.log('Maps native constructor', MSRemoteLoggerCategory.MAPS_NATIVE_SERVICE);
  }

  async init(googleMapController: GoogleMapsController, center: MSLatLng, points: any): Promise<any> {
    this.controller = googleMapController;
    this.points = points;

    this.addSubscriptions();
    return this.initMap(center, points);
  }

  private async getCameraConfig(center?: MSLatLng, points?: MSLatLng[]): Promise<MSCameraConfig> {
    if (points) {
      const cameraConfig = await this.controller.getCameraConfigForPoints(points);
      return cameraConfig;
    }

    if (center) {
      const cameraConfig = {
        center,
        zoom: CONSTANTS.MAPS.PLACE_MAP_ZOOM
      };
      return cameraConfig;
    }

    const location = await this.getCurrentLocation();
    if (location) {
      const config = {
        center: location,
        zoom: CONSTANTS.MAPS.PLACE_MAP_ZOOM
      };
      return config;
    }

    this.logger.log('Error getting current location from geolocation plugin.', MSRemoteLoggerCategory.MAPS_NATIVE_SERVICE);
    const cameraConfig = {
      center: CONSTANTS.MAPS.DEFAULT_MAP_CENTER,
      zoom: CONSTANTS.MAPS.DEFAULT_MAP_ZOOM
    };
    return cameraConfig;
  }

  async initMap(center?: MSLatLng, points?: MSLatLng[]): Promise<any> {
    const cameraConfig = await this.getCameraConfig(center, points);

    await this.controller.setZoom(cameraConfig.zoom);
    await this.controller.setCenter(cameraConfig.center);
  }

  async setMapZoom(zoom: number): Promise<any> {
    await this.controller.setZoom(zoom);
  }

  getMapZoom(): number {
    return this.currentZoom;
  }

  async setMapCenter(center: MSLatLng): Promise<any> {
    this.controller.setCenter(center);
  }

  getMapCenter(): MSLatLng {
    const center = this.cameraCenter;
    return center;
  }

  async setMapBounds(bounds: MSLatLng[]): Promise<any> {
    // this.controller.zoomToPoints(bounds);
    this.controller.fitBounds(bounds);
  }

  async removeMap() {
    await this.clearAsync();
  }

  async addInfoWindow(opts: { markerIdx: number; content: { title: string; snippet: string } }) {
    await this.markers[opts.markerIdx].setInfoContent(opts.content.title, opts.content.snippet);
    this.infoWindows.push(opts);
  }

  updateMarker(a: any) {
    throw new Error('Method not implemented.');
  }

  addViewChangeEndEvent() {}

  async setMapType(mapType: MSMapType): Promise<void> {
    await this.controller.setMapType(mapType);
  }

  async showPoint(point: MSLatLng, opts?: any, mapType?: MSMapType): Promise<void> {
    const zoom = opts && opts.hasOwnProperty('zoom') ? opts.zoom : CONSTANTS.MAPS.PLACE_MAP_ZOOM;

    if (mapType) {
      await this.controller.setMapType(mapType);
    }
    await this.controller.setCenter(point);
    await this.controller.setZoom(zoom);
  }

  async showOverview(a: any, b: any, points?: MSLatLng[]): Promise<void> {
    const fitPoints = points || this.points;

    await this.controller.setMapType(MSMapType.NORMAL);
    await this.controller.fitBounds(fitPoints);
  }

  async removeMarkerAsync(idx: number) {
    await this.markers[idx].remove();
    this.markers.splice(idx, 1);
  }

  // for LD trips, add start/stop circles and markers
  async addCirclesAndMarkersAsync(points, unused) {
    await this.addCircleAsync(points[0], CONSTANTS.tripRecording.GEOFENCE_RADIUS);
    await this.addCircleAsync(points[points.length - 1], CONSTANTS.tripRecording.DWELL_METERS);
    await this.addMarkersStartStopLdAsync(points);
  }

  async clearAsync() {
    for (const marker of this.markers) {
      await marker.remove();
    }

    for (const path of this.paths) {
      await path.remove();
    }

    for (const circle of this.circles) {
      await circle.remove();
    }

    this.markers = [];
    this.points = [];
    this.infoWindows = [];
    this.circles = [];
    this.paths = [];
  }

  //Leaving opts and page for backwards compatibility
  async addPathAsync(survey: any, points: MSLatLng[], opts?: { strokeColor: typeof POLYLINE_MERGE_COLOR }, page?: string): Promise<void> {
    //TODO: Copying logic that was previously set in place. Revise when possible
    if (survey.addedTripType === 'LOOP') {
      //await this.addLoopPolygon(points);
      await this.addPathAsyncActual(points, POLYLINE_COLOR);
    } else if (page === 'merge') {
      await this.addPathAsyncActual(points, POLYLINE_MERGE_COLOR);
    } else if (!survey.rosterAdded) {
      await this.addPathAsyncActual(points, POLYLINE_COLOR);
    }
  }

  async addMarkerAsync(markerConfig: MarkerConfig): Promise<void> {
    const marker = await this.controller.addMarker(markerConfig);
    const mapsNativeMarker = new MapsNativeMarker(marker);
    this.markers.push(mapsNativeMarker);
  }

  async addCircleAsync(center: MSLatLng, radius: number) {
    const config: CircleConfig = {
      center,
      radius,
      strokeColor: '#FF0000CC',
      strokeWeight: 2,
      fillColor: '#FF000033',
      fillOpacity: 0,
      strokeOpacity: 1,
      zIndex: 0
    };

    const circle = await this.controller.addCircle(config);
    this.circles.push(circle);
  }

  // for SD full trip paths, add start/stop markers and path
  async addMarkersAndPathAsync(points: MSLatLng[], survey: any): Promise<void> {
    await this.addMarkersStartStopAsync(points, survey);
    await this.addPathAsync(survey, points);
  }

  private async addPathAsyncActual(points: MSLatLng[], color: string): Promise<void> {
    const polylineConfig: PolylineConfig = {
      geodesic: true,
      color,
      width: POLYELINE_WIDTH,
      path: points
    };

    const polyline = await this.controller.addPolyline(polylineConfig);
    this.paths.push(polyline);
  }

  private async addMarkersStartStopAsync(points: MSLatLng[], survey: any): Promise<void> {
    const startPoint = points[0];
    const stopPoint = points[points.length - 1];

    const isLoopTrip = survey.addedTripType === 'LOOP';
    const startIconUrl = isLoopTrip ? this.assetWrapper.markerStartStop : this.assetWrapper.markerStart;
    const stopIconUrl = isLoopTrip ? this.assetWrapper.markerGeneric : this.assetWrapper.markerStop;

    const isSameAddress = await this.geocoderService.isSameAddress(startPoint, stopPoint);

    if (isSameAddress) {
      await this.addMarkerWithIcon(startPoint, this.assetWrapper.markerStartStop);
      return;
    }

    await this.addMarkerWithIcon(startPoint, startIconUrl);
    await this.addMarkerWithIcon(stopPoint, stopIconUrl);
  }

  private async addMarkersStartStopLdAsync(points: MSLatLng[]) {
    const startPoint = points[0];
    const endPoint = points[points.length - 1];

    await this.addMarkerWithIcon(startPoint, this.assetWrapper.markerStart);
    await this.addMarkerWithIcon(endPoint, this.assetWrapper.markerStop);
  }

  private async addMarkerWithIcon(coordinate: MSLatLng, iconUrl: string): Promise<void> {
    const config: MarkerConfig = {
      coordinate,
      iconUrl
    };
    await this.addMarkerAsync(config);
  }

  async addLoopPolygon(points: MSLatLng[]) {
    if (points.length < 2) {
      return;
    }

    const toPoints = points.map((point, index) => {
      if (index === 0) {
        return offsetLatLngAlongPerpendicular(null, point, points[1], LOOP_OUTLINE_OFFSET);
      }
      if (index === points.length - 1) {
        return offsetLatLngAlongPerpendicular(points[index - 1], point, null, LOOP_OUTLINE_OFFSET);
      }
      return offsetLatLngAlongPerpendicular(points[index - 1], point, points[index + 1], LOOP_OUTLINE_OFFSET);
    });

    const fromPoints = points.map((point, index) => {
      if (index === 0) {
        return offsetLatLngAlongPerpendicular(null, point, points[1], -LOOP_OUTLINE_OFFSET);
      }
      if (index === points.length - 1) {
        return offsetLatLngAlongPerpendicular(points[index - 1], point, null, -LOOP_OUTLINE_OFFSET);
      }
      return offsetLatLngAlongPerpendicular(points[index - 1], point, points[index + 1], -LOOP_OUTLINE_OFFSET);
    });

    const polygonPoints = toPoints.concat(fromPoints.reverse());
    const polygonConfig: PolygonConfig = {
      color: POLYLINE_COLOR,
      width: 2,
      path: polygonPoints,
      geodesic: false
    };

    const polygon = await this.controller.addPolygon(polygonConfig);
    this.paths.push(polygon);
  }

  private async getCurrentLocation(): Promise<MSLatLng | undefined> {
    let status = await this.geolocationService.requestPermission();
    if (status === GeolocationPermission.MSPermissionStatus.PROMPT_WITH_RATIONAL) {
      status = await this.geolocationService.requestPermission();
    }

    if (status === GeolocationPermission.MSPermissionStatus.GRANTED) {
      const locationStatus = await this.geolocationService.getCurrentPosition();
      if (locationStatus instanceof GeolocationStatus.CurrentPosition) {
        const latLng = locationStatus;
        return latLng;
      }
    }

    return undefined;
  }

  private addSubscriptions() {
    this.clearSubscriptions();
    const cameraIdleSubscription = this.controller.onCameraIdleSignal.subscribe(() => {
      this.pubSub.publish(CONSTANTS.EVENTS.mapViewChanged);
    });
    this.subscriptions.push(cameraIdleSubscription);

    const cameraDataSubscription = this.controller.cameraState.subscribe(async (cameraData) => {
      this.currentZoom = cameraData.zoom;
      this.cameraCenter = cameraData.center;
    });
    this.subscriptions.push(cameraDataSubscription);
  }

  private clearSubscriptions() {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
    this.subscriptions = [];
  }
}
